import { Alert, Box, Button, Link, TextField } from '@mui/material';
import { type FormEvent, useState } from 'react';
import { HttpError } from 'react-admin';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../utils/helperUtils.ts';
import { setPasswordWithLink } from './signupApi.ts';
import { forgetLinkSecret } from './useLinkSecret.ts';

// The API's floor too, so every password set here can sign in.
const MIN_LENGTH = 8;

type SetPasswordFormProps = {
  token: string;
  // Shown read-only when known, so password managers save it with the password.
  email?: string;
};

/** Sets the password from a link, then opens the dashboard signed in. */
const SetPasswordForm = ({ token, email }: SetPasswordFormProps) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // A refused link gets a way to the sign-in page; a typo in the form does not.
  const [error, setError] = useState<{ message: string; signIn: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < MIN_LENGTH) {
      setError({ message: `Use at least ${MIN_LENGTH} characters.`, signIn: false });
      return;
    }
    if (password !== confirm) {
      setError({ message: 'The passwords do not match.', signIn: false });
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await setPasswordWithLink(token, password);
      forgetLinkSecret('token');
      forgetLinkSecret('session_id');
      navigate('/', { replace: true });
    } catch (err) {
      // A refused link stays refused; a network error may not.
      if (err instanceof HttpError && err.status === 400) {
        forgetLinkSecret('token');
      }
      setError({ message: getErrorMessage(err), signIn: true });
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit} noValidate sx={{ mt: 2, textAlign: 'left' }}>
      {email && (
        <TextField
          label="Email"
          value={email}
          autoComplete="username"
          fullWidth
          margin="dense"
          slotProps={{ input: { readOnly: true } }}
        />
      )}
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        autoFocus
        fullWidth
        margin="dense"
        helperText={`At least ${MIN_LENGTH} characters`}
      />
      <TextField
        label="Confirm password"
        type="password"
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
        autoComplete="new-password"
        fullWidth
        margin="dense"
      />
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error.message}
          {error.signIn && (
            <>
              {' '}
              <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
                Sign in
              </Link>
            </>
          )}
        </Alert>
      )}
      <Button type="submit" variant="contained" fullWidth disabled={saving} sx={{ mt: 2 }}>
        {saving ? 'Signing you in…' : 'Set password and sign in'}
      </Button>
    </Box>
  );
};

export default SetPasswordForm;
